import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Input,
  message,
  Tooltip,
} from 'antd';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import LadaNumero from 'components/LadaNumero';
import ImageUploader from 'components/ImageUploader';
import { CameraOutlined, FieldTimeOutlined } from '@ant-design/icons';
import makeMessager from 'utils/complexFormsMessages';
import { getConfiguracionDeEntidad, postConfiguracionDeEntidad } from 'api/catalogos/configuracionDeEntidad';
import { getMonedas } from 'api/configuracion';
import { getTipoDeCargo, getTiposDeCargos } from 'api/recaudacion/tiposDeCargo';
import { getConceptoDeIngresos, getConceptosDeIngresos } from 'api/catalogos/conceptosDeIngresos';
import { InputSearchPro } from 'components/InputSearch';
import {
  getCiudadanos,
  patchCiudadano,
  getCiudadano,
} from 'api/cuentaunicasir';
import { getGrupos } from 'api/catalogos/grupos';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import PlainTransfer from 'components/PlainTransfer';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { getCatalogosDeRedondeo } from 'api/catalogos';
// import { getUnidadDePlazoCita } from 'api/tramites';
import NumericInput from 'components/NumericInput';
import { titleize, toMoment } from 'utils/normalizers';
import { getEstadosDeInfracciones } from 'api/catalogos/estadosDeInfracciones';
import TiposDeRecargosOmitidos, { permissionConfig as permissionTiposDeRecargosOmitidos } from 'components/Entity/Configurations/TiposDeRecargosOmitidos';
import { getPermission } from 'utils/permission';

export const entityConfigPermission = {
  title: 'Configuración de la entidad',
  model: 'configuraciondelaentidad',
  catalogs: [
    'lada',
    'moneda',
    'tipoderedondeo',
    'catalogodemetododepago',
    'padron',
    'configuraciondeusodelfolio',
    'tipodecargo',
    'conceptodeingreso',
    'group',
    'unidaddeplazocita',
    'tipodedescuento',
  ],
};

const unidadDePlazoEnum = Object.freeze({
  MINUTOS: 2,
  HORAS: 3,
  DIAS: 4,
  SEMANAS: 5,
  MESES: 6,
});

const descripcionNormalizer = (val) => (val === 'DIAS' ? 'Días' : titleize(val));

const unidadesDePlazo = Object.entries(unidadDePlazoEnum)
  .map(([desc, id]) => ({ id, descripcion: descripcionNormalizer(desc) }));

const unidadPlazoToMomentUnit = Object.freeze({
  [unidadDePlazoEnum.MINUTOS]: 'minutes',
  [unidadDePlazoEnum.HORAS]: 'hours',
  [unidadDePlazoEnum.DIAS]: 'day',
  [unidadDePlazoEnum.SEMANAS]: 'week',
  [unidadDePlazoEnum.MESES]: 'month',
});

const Configurations = ({
  setLoading,
  callback,
  setCurrentTabKey,
  setForm,
  disabled,
  folios,
  foliosRecibos,
  metodos,
  padrones,
}) => {
  const recargosOmitidosPermission = useMemo(() => getPermission(permissionTiposDeRecargosOmitidos),
    []);
  const [form] = Form.useForm();
  const _utilizar_redondeo = Form.useWatch('utilizar_redondeo', form);
  const [monedas, setMonedas] = useState([]);
  const _entidad = useSelector(({ auth }) => auth.entidad);
  const catalogosDeRedondeo = useSelector(({ catalogs }) => catalogs.catalogosDeRedondeo);
  const [soloNetPay, setSoloNetPay] = useState(false);
  const [allowPublicoGeneral, setAllowPublicoGeneral] = useState(false);
  const _unidad_agenda_cita_desde = Form.useWatch('unidad_agenda_cita_desde', form);
  const _cantidad_agenda_cita_desde = Form.useWatch('cantidad_agenda_cita_desde', form);
  const _unidad_agenda_cita_hasta = Form.useWatch('unidad_agenda_cita_hasta', form);
  const _cantidad_agenda_cita_hasta = Form.useWatch('cantidad_agenda_cita_hasta', form);

  const [ciudadanoFound, setCiudadanoFound] = useState([]);
  const [dataExterno, setDataExterno] = useState({ results: [] });
  const [visibleBusquedaAvanzada, setVisibleBusquedaAvanzada] = useState(false);
  const [tiposDeDescuentos, setTiposDeDescuentos] = useState([]);
  const [conceptosIngresoTemp, setConceptosIngresoTemp] = useState({ results: [] });
  const [tiposDeCargoTemp, setTiposDeCargoTemp] = useState({ results: [] });
  const [roles, setRoles] = useState([]);
  const [estadoDeLaInfraccion, setEstadoDeLaInfraccion] = useState([]);

  const conceptosDeIngresos = useRef({ results: [] });
  const tiposDeCargo = useRef({ results: [] });
  const sortOptions = [
    { id: true, descripcion: 'Ascendente' },
    { id: false, descripcion: 'Descendente' },
  ];
  const now = toMoment(new Date());

  const asignarEntidad = async (_user) => {
    setLoading(true);
    const { razon_social } = _entidad;
    const { entidad, nombre_completo, id } = _user;
    entidad.push(_entidad.id);
    await patchCiudadano(
      id,
      { entidad },
      `Ahora el usuario ${nombre_completo} también pertenece a ${razon_social}.`,
    );

    setLoading(false);
  };

  const msgAsignarEntidad = (_user) => (
    message.info({
      content: (
        <>
          <Row style={{ width: '100%' }}>
            El contribuyente no pertenece a la entidad.
          </Row>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              ¿Desea asignarlo a esta entidad?
            </strong>
          </Row>
          <Row align="middle" justify="center" style={{ width: '100%' }}>
            <Button
              type="link"
              onClick={() => asignarEntidad(_user)}
            >
              Si
            </Button>
            <Button
              onClick={() => {
                message.destroy();
                form.resetFields();
              }}
              type="link"
            >
              No
            </Button>
          </Row>
        </>

      ),
      duration: 0,
    })
  );

  const getCargo = async (id) => {
    if (id) {
      const _tipoDeCargo = await getTipoDeCargo(id);
      setTiposDeCargoTemp({
        count: 1,
        next: null,
        previous: null,
        results: [_tipoDeCargo],
      });
    }
  };

  const getRoles = async () => {
    const _roles = await getGrupos();
    setRoles(_roles);
  };

  const getConcepto = async (id) => {
    if (id) {
      const _conceptoDeIngresos = await getConceptoDeIngresos(id);
      setConceptosIngresoTemp({
        count: 1,
        next: null,
        previous: null,
        results: [_conceptoDeIngresos],
      });
    }
  };

  const _getCiudadano = async (id) => {
    const _ciudadano = await getCiudadano(id);
    setCiudadanoFound([_ciudadano]);
  };

  const fetchDescuentos = async () => {
    const _tiposDeDescuentos = await getTiposDeDescuentos({
      tipo_de_aplicacion: 1,
      es_especial: false,
    });
    setTiposDeDescuentos(_tiposDeDescuentos);
  };

  useEffect(() => {
    if (_entidad) {
      const {
        lada,
        moneda,
        utilizar_redondeo,
        foliador,
        solo_pago_netpay,
        tipo_de_cargo,
        concepto_de_ingreso_redondeo,
        metodo_de_pago_principal,
        ciudadano_publico_en_general,
        permite_publico_en_general,
        padron_principal,
        catalogos_de_folio_recibo,
        solo_mayusculas,
        exigir_cajero_en_depositos_bancarios,
        tipos_de_descuentos,
        ordenar_cargos_acendente,
        tipo_de_redondeo,
        rol_inspector,
        aplica_diferencia_pagos_externos,
        cantidad_tope_pagos_externos,
        consultar_infracciones_viales_relacionadas,
        visualizar_redondeo,
        dia_de_mes_para_actualizacion_inpc,
        es_dia_inhabil_el_sabado,
        es_dia_inhabil_el_domingo,
        agente_usuario,
        aplica_a_padrones,
        aplica_aqc,
        aplica_calificar_estatus,
        aplica_folio_manual,
        aplica_fundamento_legal,
        aplica_infraccion_peatonal,
        aplica_infractor_manual,
        aplica_sectores,
        aplica_serie,
        aplican_acuses,
        aplican_advertencias,
        estados_de_la_infraccion,
        foliador_acreditacion,
      } = _entidad;

      getCargo(tipo_de_cargo);
      getConcepto(concepto_de_ingreso_redondeo);
      fetchDescuentos();
      getCatalogosDeRedondeo();
      getRoles();
      const _allowPublicoGeneral = permite_publico_en_general || false;
      setAllowPublicoGeneral(_allowPublicoGeneral);
      if (_allowPublicoGeneral && ciudadano_publico_en_general !== null) {
        _getCiudadano(ciudadano_publico_en_general);
      }
      form.setFieldsValue({
        lada,
        moneda: moneda?.id,
        utilizar_redondeo,
        foliador,
        solo_pago_netpay,
        tipo_de_cargo,
        concepto_de_ingreso_redondeo,
        metodo_de_pago_principal,
        ciudadano_publico_en_general,
        permite_publico_en_general,
        padron_principal,
        catalogos_de_folio_recibo,
        solo_mayusculas,
        exigir_cajero_en_depositos_bancarios,
        tipos_de_descuentos,
        ordenar_cargos_acendente,
        tipo_de_redondeo,
        rol_inspector,
        aplica_diferencia_pagos_externos,
        cantidad_tope_pagos_externos,
        consultar_infracciones_viales_relacionadas,
        visualizar_redondeo,
        dia_de_mes_para_actualizacion_inpc: dia_de_mes_para_actualizacion_inpc || 0,
        es_dia_inhabil_el_sabado,
        es_dia_inhabil_el_domingo,
        agente_usuario,
        aplica_a_padrones,
        aplica_aqc,
        aplica_calificar_estatus,
        aplica_folio_manual,
        aplica_fundamento_legal,
        aplica_infraccion_peatonal,
        aplica_infractor_manual,
        aplica_sectores,
        aplica_serie,
        aplican_acuses,
        aplican_advertencias,
        estados_de_la_infraccion,
        foliador_acreditacion,
      });
    }
  }, [_entidad]);
  const onFinish = async (_continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const _configEntidad = await postConfiguracionDeEntidad(values);
      if (_configEntidad) {
        setTimeout(async () => callback(_configEntidad, _continue));
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const showMsg = makeMessager(setCurrentTabKey);

  const onFinishHandler = async (tabKey = null, _continue = true) => {
    try {
      if (tabKey) {
        showMsg(tabKey);
      } else {
        await onFinish(_continue);
      }
    } catch (err) {
      onError(err);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      const _monedas = await getMonedas();
      const _estadoInfraccion = await getEstadosDeInfracciones();
      await getConfiguracionDeEntidad();
      if (mounted) {
        setMonedas(_monedas.filter((e) => !e.calculable));
        setEstadoDeLaInfraccion(_estadoInfraccion);
        setLoading(false);
      }
    };
    fetch();
    setTimeout(() => {
      form.onFinishHandler = onFinishHandler;
      setForm(form);
    });
    return () => { mounted = false; };
  }, []);

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    lada: [requiredRule],
    url: [
      {
        type: 'url',
        message: 'Ingrese una URL válida',
      },
    ],
  };

  const resetAndValidate = () => {
    setCiudadanoFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const searchCiudadano = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _ciudadanos = await getCiudadanos({ q });
        if ([1].includes(_ciudadanos.results.length)) {
          const [selected] = _ciudadanos.results;
          form.setFieldsValue({
            ciudadano_publico_en_general: selected.id,
          });
          setCiudadanoFound([selected]);
        } else if (_ciudadanos.results.length > 1) {
          setDataExterno(_ciudadanos);
          setVisibleBusquedaAvanzada(true);
        } if (!_ciudadanos.results.length) {
          message.info('No se encontró ciudadano');
          setVisibleBusquedaAvanzada(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const getFromDateToDate = () => {
    const valid = (val) => val || val === 0;

    const {
      unidad_agenda_cita_desde,
      cantidad_agenda_cita_desde,
      unidad_agenda_cita_hasta,
      cantidad_agenda_cita_hasta,
    } = form.getFieldsValue();

    const tooltipMessage = (when, info = '') => `Ingrese "Unidad de tiempo para agendar ${when}" y "Cantidad de tiempo para agendar ${when}" para calcular la fecha ${when} ${info}`;

    const momentUnitDesde = unidadPlazoToMomentUnit[unidad_agenda_cita_desde];
    const _fromDate = valid(unidad_agenda_cita_desde)
      ? now.clone().add(cantidad_agenda_cita_desde, momentUnitDesde) : tooltipMessage('desde');

    const momentUnitHasta = unidadPlazoToMomentUnit[unidad_agenda_cita_hasta];
    const _toDate = valid(cantidad_agenda_cita_hasta) ? now.clone()
      : tooltipMessage('hasta', '(dependiendo la oficina seleccionada esta fecha podría cambiar)');
    if (typeof _toDate !== 'string') {
      if (unidad_agenda_cita_hasta > 4) _toDate.endOf(momentUnitHasta);
      _toDate.add(cantidad_agenda_cita_hasta, momentUnitHasta);
    }
    return [
      typeof _fromDate === 'string' ? _fromDate : _fromDate.format(`DD/MM/YYYY ${!_fromDate.isSame(now, 'd') ? '[a partir de la hora de entrada del primer horario de la oficina]' : 'a am'}`),
      typeof _toDate === 'string' ? _toDate : _toDate.format('DD/MM/YYYY [hasta la hora de salida del último horario de la oficina]'),
    ];
  };

  const [fromDate, toDate] = useMemo(
    getFromDateToDate,
    [
      _unidad_agenda_cita_desde,
      _cantidad_agenda_cita_desde,
      _unidad_agenda_cita_hasta,
      _cantidad_agenda_cita_hasta,
    ],
  );
  return (
    <Col span={24}>
      <Form
        form={form}
        name="configurations"
        layout="vertical"
        onFinish={() => onFinish()}
        initialValues={{
          permite_publico_en_general: false,
          ordenar_cargos_acendente: false,
        }}
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <ImageUploader
              form={form}
              formItemLabel="Logo Vertical"
              formItemName="logo_vertical"
              imageURL={form.getFieldValue('logo_vertical')}
              icon={<CameraOutlined />}
              allowClear={false}
              disabled={disabled}
              height={128}
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <LadaNumero rules={rules} disabled={disabled} ladaOnly form={form} />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              rules={rules.required}
              name="moneda"
              label="Moneda"
            >
              <Select
                disabled={disabled}
                dataSource={monedas}
                keyLabelRender
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="solo_mayusculas"
              label="¿Solo Mayúsculas?"
            >
              <Select
                disabled={disabled}
                trueFalse
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="exigir_cajero_en_depositos_bancarios"
              label="¿Exigir Cajero en Depósitos Bancarios?"
            >
              <Select
                disabled={disabled}
                trueFalse
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="visualizar_redondeo"
              rules={rules.required}
              label="¿Visualizar redondeo?"
            >
              <Select disabled={disabled} trueFalse />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="utilizar_redondeo"
              rules={rules.required}
              label="¿Usar redondeo?"
            >
              <Select disabled={disabled} trueFalse />
            </Form.Item>
          </Col>
          {_utilizar_redondeo && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="tipo_de_redondeo"
                rules={rules.required}
                label="Tipo de Redondeo"
              >
                <Select disabled={disabled} dataSource={catalogosDeRedondeo} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="ordenar_cargos_acendente"
              rules={rules.required}
              label="Orden de los Cargos"
            >
              <Select disabled={disabled} dataSource={sortOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="metodo_de_pago_principal"
              label="Método de pago principal"
            >
              <Select
                disabled={disabled}
                dataSource={metodos}
                labelProp="concepto"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="solo_pago_netpay"
              rules={rules.required}
              label="¿Solo pago por netpay?"
            >
              <Select
                disabled={disabled}
                trueFalse
                onChange={setSoloNetPay}
              />
            </Form.Item>
          </Col>
          {soloNetPay && (
            <>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="esquema_netpay"
                  label="Esquma netpay"
                  rules={rules.required}

                >
                  <Input
                    allowClear
                    disabled={disabled}

                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="host_de_netpay"
                  label="Host netpay"
                  rules={rules.required}
                >
                  <Input
                    allowClear
                    disabled={disabled}

                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="ruta_de_netpay"
                  label="Ruta Netpay"
                  rules={rules.required}

                >
                  <Input
                    allowClear
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="contrasenia_de_netpay"
                  label="Contraseña de netpay"
                  rules={rules.required}
                >
                  <Input
                    allowClear
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="usuario_de_netpay"
                  label="Usuario de Netpay"
                  rules={rules.required}

                >
                  <Input
                    allowClear
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="identificador_de_tienda"
                  label="Identificador de Tienda"
                  rules={rules.required}

                >
                  <Input
                    allowClear
                    disabled={disabled}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="url_qr"
                  label="URL de QR"
                  tooltip="Determina la url de los qr"
                  rules={rules.url}

                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="url_terminos_y_condiciones"
                  label="URL de Terminos y Condiciones"
                  tooltip="Url utilizada para los terminos de uso y condiciones de privacidad del portal web"
                  rules={rules.url}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
            </>
          )}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="padron_principal"
              rules={rules.required}
              label="Padrón Principal"
            >
              <Select disabled={disabled} dataSource={padrones.filter((e) => e.id !== 5)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="catalogos_de_folio_recibo"
              rules={rules.required}
              label="Catalogo de folio recibo"
            >
              <Select disabled={disabled} dataSource={foliosRecibos} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="foliador"
              rules={rules.required}
              label="Foliador para Ciudadanos"
            >
              <Select disabled={disabled} dataSource={folios} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="foliador_cancelador"
              label="Folio Cancelador de Recibo"
            >
              <Select disabled={disabled} dataSource={folios} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="foliador_acreditacion"
              label="Foliador de acreditación"
            >
              <Select disabled={disabled} dataSource={folios} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="tipo_de_cargo"
              label="Tipo de cargo"
              tooltip="Tipo de cargo para depósitos en garantías, el tipo de cargo no debe ser un accesorio, de tipo de aplicación capturable y de padrón ciudadano."
            >
              <Select
                dataSource={
                  tiposDeCargoTemp.results?.length
                    ? tiposDeCargoTemp : tiposDeCargo.current
                }
                usingExternalDataSource={Boolean(tiposDeCargoTemp.results?.length)}
                onClear={() => { form.resetFields(['tipo_de_cargo']); setTiposDeCargoTemp({ results: [] }); }}
                pagination={{
                  getter: getTiposDeCargos,
                  setter: (dataSource) => {
                    tiposDeCargo.current = dataSource;
                  },
                  config: {
                    auto: true,
                    qName: 'descripcion',
                    params: {
                      tipo_de_aplicacion: 1,
                      padron: 1,
                    },
                  },
                }}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="concepto_de_ingreso_redondeo"
              label="Concepto de ingreso"
              tooltip="Concepto de ingreso para redondeo"
            >
              <Select
                dataSource={
                  conceptosIngresoTemp.results?.length
                    ? conceptosIngresoTemp : conceptosDeIngresos.current
                }
                usingExternalDataSource={Boolean(conceptosIngresoTemp.results?.length)}
                labelProp="descripcion"
                onClear={() => {
                  form.resetFields(['concepto_de_ingreso_redondeo']);
                  setConceptosIngresoTemp({ results: [] });
                }}
                pagination={{
                  getter: getConceptosDeIngresos,
                  setter: (dataSource) => {
                    conceptosDeIngresos.current = dataSource;
                  },
                  config: { auto: true, qName: 'descripcion' },
                }}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="permite_publico_en_general"
              label="Permite Cobro A Público en General"
            >
              <Select disabled={disabled} trueFalse onChange={setAllowPublicoGeneral} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="rol_inspector"
              label="Rol de inspector"
            >
              <Select dataSource={roles} labelProp="name" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="aplica_diferencia_pagos_externos"
              label="Aplica diferencia de pagos externos"
            >
              <Input allowClear disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <NumericInput
              name="cantidad_tope_pagos_externos"
              label="Tope de Pagos"
              disabled={disabled}
            />
          </Col>
          {allowPublicoGeneral && (
            <InputSearchPro
              disabled={disabled}
              label="Ciudadano Publico General"
              name="q"
              onClickAdvanced={() => setVisibleBusquedaAvanzada(true)}
              breakPoints={{
                xs: 24,
                sm: 24,
                md: 16,
              }}
              inputProps={{
                onPressEnter: () => searchCiudadano(),
                onChange: ({ target: { value } }) => {
                  if (!value) {
                    resetAndValidate();
                  }
                },
              }}
              resultProps={{
                name: 'ciudadano_publico_en_general',
                label: 'Ciudadano Público General',
                dataSource: ciudadanoFound,
                labelProp: 'nombre_completo',
                onClear: resetAndValidate,
                breakPoints: {
                  xs: 24,
                  sm: 24,
                  md: 16,
                },
              }}
            />
          )}
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="consultar_infracciones_viales_relacionadas"
              label="Consultar infracciones relacionadas"
              tooltip="Determina si se consultan infracciones directamente o si se muestra el modal para seleccionar la infracción correcta."
            >
              <Select trueFalse disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="es_dia_inhabil_el_sabado"
              label="¿Es día inhábil el sabado?"
            >
              <Select
                disabled={disabled}
                trueFalse
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="es_dia_inhabil_el_domingo"
              label="¿Es día inhábil el Domingo?"
            >
              <Select
                disabled={disabled}
                trueFalse
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="unidad_agenda_cita_desde"
              label="Unidad de tiempo para agendar desde"
              tooltip="Determina (junto al campo 'Cantidad de tiempo para agendar desde') a partir de cuanto tiempo en el futuro se puede agendar una cita. Ejemplo: Unidad: Día y Cantidad: 1, determina que al momento de agendar una cita solo se pueden agendar a partir del día siguiente"
            >
              <Select dataSource={unidadesDePlazo} disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="cantidad_agenda_cita_desde"
              label="Cantidad de tiempo para agendar desde"
              tooltip="Determina (junto al campo 'Cantidad de tiempo para agendar desde') a partir de cuanto tiempo en el futuro se puede agendar una cita. Ejemplo: Unidad: Día y Cantidad: 1, determina que al momento de agendar una cita solo se pueden agendar a partir del día siguiente"
            >
              <Input
                allowClear
                disabled={disabled}
                addonAfter={(<Tooltip title={fromDate}><FieldTimeOutlined /></Tooltip>)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="unidad_agenda_cita_hasta"
              label="Unidad de tiempo para agendar hasta"
              tooltip="Determina (junto al campo 'Cantidad de tiempo para agendar hasta') hasta cuanto tiempo en el futuro se puede agendar una cita. Ejemplo: Unidad: Mes y Cantidad: 1, determina que las citas pueden ser agendadas durante el mes actual y el mes siguiente."
            >
              <Select
                dataSource={unidadesDePlazo.filter((e) => e.id >= unidadDePlazoEnum.DIAS)}
                disabled={disabled}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="cantidad_agenda_cita_hasta"
              label="Cantidad de tiempo para agendar hasta"
              tooltip="Determina (junto al campo 'Cantidad de tiempo para agendar hasta') hasta cuanto tiempo en el futuro se puede agendar una cita. Ejemplo: Unidad: Mes y Cantidad: 1, determina que las citas pueden ser agendadas durante el mes actual y el mes siguiente. Ingrese 0 para definir solo para el periodo actual"
            >
              <Input
                allowClear
                disabled={disabled}
                addonAfter={(<Tooltip title={toDate}><FieldTimeOutlined /></Tooltip>)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="dia_de_mes_para_actualizacion_inpc"
              label="Día del mes para actualizar INPC"
              tooltip="Determina el día del mes actual en el que se actualiza el INPC y se valida la fecha limite de la ORDEN DE PAGO."
            >
              <Input allowClear disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="estados_de_la_infraccion"
              label="Estado de la infracción"
            >
              <Select
                dataSource={estadoDeLaInfraccion}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <TiposDeRecargosOmitidos
              permission={recargosOmitidosPermission}
            />
          </Col>
          <Col span={24}>
            <PlainTransfer
              dataSource={tiposDeDescuentos}
              label="Tipo de descuento"
              formItemName="tipos_de_descuentos"
              form={form}
              filterProp="descripcion"
              disabled={disabled}
            />
          </Col>
          <Form.Item name="id" hidden>
            <Input allowClear />
          </Form.Item>
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Row>
      </Form>

      <BusquedaAvanzadaPadrones
        visible={visibleBusquedaAvanzada}
        setVisible={setVisibleBusquedaAvanzada}
        callback={(found) => {
          setCiudadanoFound([found]);
          form.setFieldsValue({
            ciudadano_publico_en_general: found.id,
          });
        }}
        dataExterno={dataExterno}
        setDataExterno={setDataExterno}
        tipoDePadron={1}
        msgAsignarEntidad={msgAsignarEntidad}
        allowAdd={false}
      />
    </Col>
  );
};

Configurations.propTypes = {
  callback: PropTypes.func,
  setLoading: PropTypes.func.isRequired,
  setForm: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  setCurrentTabKey: PropTypes.func.isRequired,
  folios: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  foliosRecibos: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  metodos: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  padrones: PropTypes.arrayOf(PropTypes.shape()).isRequired,

};

Configurations.defaultProps = {
  callback: () => { },
  disabled: false,
};

export default Configurations;
